import React, { StrictMode, Suspense, lazy } from 'react';
import '@/lib/errorReporter';
import { enableMapSet } from "immer";
enableMapSet();
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/react';
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoader } from '@/components/PageLoader';
import { RootLayout } from '@/components/RootLayout';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import { attemptStaleChunkReload, clearStaleChunkMarker, isStaleChunkError } from '@/lib/stale-chunk';
import '@/index.css'

// lazyPage: like React.lazy, but when a deploy has replaced the hashed chunks
// out from under an open tab (the import then fails), reload once to pick up
// the fresh build instead of surfacing a routing error page.
function lazyPage<T extends React.ComponentType>(importer: () => Promise<{ default: T }>) {
  return lazy(() =>
    importer()
      .then((module) => {
        clearStaleChunkMarker();
        return module;
      })
      .catch((error) => {
        if (isStaleChunkError(error) && attemptStaleChunkReload()) {
          // Keep the suspense fallback on screen while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }),
  );
}

const HomePage = lazyPage(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const UserDashboardPage = lazyPage(() => import('@/pages/UserDashboardPage').then((m) => ({ default: m.UserDashboardPage })));
const MapPage = lazyPage(() => import('@/pages/MapPage').then((m) => ({ default: m.MapPage })));
const DirectoryPage = lazyPage(() => import('@/pages/DirectoryPage').then((m) => ({ default: m.DirectoryPage })));
const ElephantsPage = lazyPage(() => import('@/pages/ElephantsPage').then((m) => ({ default: m.ElephantsPage })));
const PrivacyPolicyPage = lazyPage(() => import('@/pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazyPage(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const CookiePolicyPage = lazyPage(() => import('@/pages/CookiePolicyPage').then((m) => ({ default: m.CookiePolicyPage })));
const DisclaimerPage = lazyPage(() => import('@/pages/DisclaimerPage').then((m) => ({ default: m.DisclaimerPage })));
const DataDeletionPage = lazyPage(() => import('@/pages/DataDeletionPage').then((m) => ({ default: m.DataDeletionPage })));
const ContributePage = lazyPage(() => import('@/pages/ContributePage').then((m) => ({ default: m.ContributePage })));
const LiveFeedPage = lazyPage(() => import('@/pages/LiveFeedPage').then((m) => ({ default: m.LiveFeedPage })));
const PooramDetailPage = lazyPage(() => import('@/pages/PooramDetailPage').then((m) => ({ default: m.PooramDetailPage })));
const ElephantDetailPage = lazyPage(() => import('@/pages/ElephantDetailPage').then((m) => ({ default: m.ElephantDetailPage })));
const AdminDashboardPage = lazyPage(() => import('@/pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })));
const NotFoundPage = lazyPage(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

const withSuspense = (node: React.ReactNode) => <Suspense fallback={<PageLoader />}>{node}</Suspense>;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,        // 30s global default — pages feel instant on back-nav
      gcTime: 5 * 60 * 1000,      // keep cache 5min after component unmounts
    },
  },
});
const router = createBrowserRouter([{
  element: <RootLayout />,
  errorElement: <RouteErrorBoundary />,
  children: [
  {
    path: "/",
    element: withSuspense(<HomePage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/dashboard",
    element: withSuspense(
      <ProtectedRoute requireRole="user">
        <UserDashboardPage />
      </ProtectedRoute>,
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/poorams/:id",
    element: withSuspense(<PooramDetailPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/map",
    element: withSuspense(<MapPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/directory",
    element: withSuspense(<DirectoryPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/elephants",
    element: withSuspense(<ElephantsPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/elephants/:id",
    element: withSuspense(<ElephantDetailPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/contribute",
    element: withSuspense(<ContributePage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/feed",
    element: withSuspense(<LiveFeedPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/privacy-policy",
    element: withSuspense(<PrivacyPolicyPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/terms",
    element: withSuspense(<TermsPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/cookie-policy",
    element: withSuspense(<CookiePolicyPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/disclaimer",
    element: withSuspense(<DisclaimerPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/data-deletion",
    element: withSuspense(<DataDeletionPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/admin",
    element: withSuspense(
      <ProtectedRoute requireRole="admin">
        <AdminDashboardPage />
      </ProtectedRoute>,
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "*",
    element: withSuspense(<NotFoundPage />),
    errorElement: <RouteErrorBoundary />,
  },
]}]);
// pk_live_ publishable keys are public-by-design and ship in every client
// bundle. We embed the production key as a fallback so the app survives a
// build that doesn't have VITE_CLERK_PUBLISHABLE_KEY in its env (e.g. the
// Cloudflare auto-deploy on push). Override via env when building for any
// other Clerk instance / domain.
const CLERK_PUBLISHABLE_KEY_FALLBACK = 'pk_live_Y2xlcmsucG9vcmFtLmFwcCQ';
const clerkPublishableKey =
  (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ?? CLERK_PUBLISHABLE_KEY_FALLBACK;

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/">
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
          {/* MED-8 fix: Toaster outside ErrorBoundary so toasts fire even after crashes */}
          <Toaster position="top-center" richColors />
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>
  );
}
